import { useEffect, useMemo, useRef, useState } from "react";
import CodeMirror from "@uiw/react-codemirror";
import { python } from "@codemirror/lang-python";
import { oneDark } from "@codemirror/theme-one-dark";

type OutputLine =
  | { kind: "stdout" | "stderr" | "system"; text: string }
  | { kind: "image"; mime: string; data: string };

let sharedWorker: Worker | null = null;
function getWorker(): Worker {
  if (!sharedWorker) {
    sharedWorker = new Worker(
      `${import.meta.env.BASE_URL}pyodide-worker.js`,
    );
  }
  return sharedWorker;
}

export function PythonSandbox(props: {
  code: string;
  onChange?: (code: string) => void;
  editorReadOnly?: boolean;
  height?: string;
}) {
  const { code, onChange, editorReadOnly, height = "320px" } = props;
  const [output, setOutput] = useState<OutputLine[]>([]);
  const [running, setRunning] = useState(false);
  const [loadingRuntime, setLoadingRuntime] = useState(true);
  const idRef = useRef(0);

  useEffect(() => {
    const worker = getWorker();
    const id = ++idRef.current;
    const onMsg = (e: MessageEvent) => {
      if (e.data?.id === id && e.data.type === "ready") {
        setLoadingRuntime(false);
      } else if (e.data?.id === id && e.data.type === "error") {
        setLoadingRuntime(false);
        setOutput((o) => [
          ...o,
          { kind: "stderr", text: `Failed to load Python runtime: ${e.data.value}\n` },
        ]);
      }
    };
    worker.addEventListener("message", onMsg);
    worker.postMessage({ id, type: "warmup" });
    return () => worker.removeEventListener("message", onMsg);
  }, []);

  const run = () => {
    if (running) return;
    setRunning(true);
    setOutput([]);
    const worker = getWorker();
    const id = ++idRef.current;
    const onMsg = (e: MessageEvent) => {
      if (e.data?.id !== id) return;
      if (e.data.type === "stdout" || e.data.type === "stderr") {
        setOutput((o) => [...o, { kind: e.data.type, text: e.data.value }]);
      } else if (e.data.type === "image") {
        setOutput((o) => [
          ...o,
          { kind: "image", mime: e.data.mime ?? "image/png", data: e.data.value },
        ]);
      } else if (e.data.type === "done") {
        setRunning(false);
        worker.removeEventListener("message", onMsg);
      }
    };
    worker.addEventListener("message", onMsg);
    worker.postMessage({ id, type: "run", code });
  };

  const extensions = useMemo(() => [python()], []);

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-xl overflow-hidden border border-border">
        <div className="flex items-center justify-between bg-[hsl(220_15%_11%)] text-white px-3 py-2 font-mono text-[11px] uppercase tracking-widest">
          <span>// python {editorReadOnly ? "(read-only)" : ""}</span>
          <button
            type="button"
            onClick={run}
            disabled={running || loadingRuntime}
            className="px-3 py-1 rounded-md bg-secondary text-secondary-foreground font-bold disabled:opacity-40 disabled:cursor-not-allowed hover:brightness-105 transition"
          >
            {loadingRuntime ? "Loading runtime…" : running ? "Running…" : "▶ Run"}
          </button>
        </div>
        <CodeMirror
          value={code}
          theme={oneDark}
          extensions={extensions}
          height={height}
          readOnly={editorReadOnly}
          onChange={(v) => onChange?.(v)}
          basicSetup={{ lineNumbers: true, highlightActiveLine: !editorReadOnly }}
        />
      </div>
      <div
        className="rounded-xl bg-[hsl(220_15%_8%)] text-white p-4 font-mono text-xs overflow-auto"
        style={{ minHeight: 80, maxHeight: 360 }}
        aria-live="polite"
      >
        <div className="text-white/40 mb-1">// output</div>
        {output.length === 0 ? (
          <div className="text-white/30">Run the code to see output here.</div>
        ) : (
          output.map((l, i) =>
            l.kind === "image" ? (
              <img
                key={i}
                src={`data:${l.mime};base64,${l.data}`}
                alt="Plot output"
                className="my-2 max-w-full rounded-md bg-white"
              />
            ) : (
              <pre
                key={i}
                className={`whitespace-pre-wrap m-0 ${l.kind === "stderr" ? "text-[hsl(14_85%_70%)]" : "text-white/90"}`}
              >
                {l.text}
              </pre>
            ),
          )
        )}
      </div>
    </div>
  );
}
